<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { PlayIcon, PlayCircleIcon, PauseIcon, ArrowPathIcon } from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { buildQueueEntry, useAudioPlaybackStore } from '@/stores/playerStore';
import { playTrackListInContext } from '@/composables/playTrackListInContext';
import { useTrackQueueContext } from '@/composables/trackQueueContext';
import { useAltKeyHeld } from '@/composables/useAltKeyHeld';
import { isSameQueueOccurrence, type QueueEntry } from '@/types/playerQueue';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';
import { toast } from 'vue-sonner';

/**
 * Reusable Play cell for tables. Three track kinds:
 *  - live_track: streams the decrypted gvlibrary mp3 via the player store.
 *  - raw_ddex:   fetches a presigned audio URL, then plays it via the player store.
 *  - spotify:    opens the Spotify app via a `spotify://track/<id>` deep link
 *                (no in-app playback — Spotify audio is not ours to serve).
 *
 * A single global <AudioPlayer /> is mounted once in SidebarLayout, so the player
 * store always has its <audio> element — pages with a Play column do not (and must
 * not) mount their own; a second instance would fight over the one shared element.
 */
const props = withDefaults(
  defineProps<{
    kind: 'live_track' | 'raw_ddex' | 'spotify';
    /** LiveTrack code — required for kind 'live_track'. */
    liveTrackCode?: string | null;
    /** RawDdexTrack id — required for kind 'raw_ddex'. */
    rawDdexTrackId?: number | string | null;
    /** Spotify track id — required for kind 'spotify'. */
    spotifyTrackId?: string | null;
    /**
     * 'icon' (default): the 28px ghost button used in table Play columns.
     * 'cover-overlay': a full-bleed click target for cover-art tiles — the whole
     * art plays, with a centered chip revealed on hover (or whenever this track
     * is playing/loading). The parent tile must be `relative group`.
     */
    variant?: 'icon' | 'cover-overlay';
    /**
     * Cover-overlay chip size. 'sm' (20px chip) is for small tiles (~44px)
     * where the default 28px chip would collide with CoverArtTile's
     * corner expand button.
     */
    chipSize?: 'default' | 'sm';
    /**
     * Whether the track has audio. Defaults to true: a Vue Boolean prop that is absent
     * is cast to false, which would wrongly disable every Play button that doesn't pass
     * this (e.g. all Track Matching rows). Callers that genuinely know there's no audio
     * (the search page's raw-ddex rows) pass false explicitly.
     */
    hasAudio?: boolean;
    /**
     * Overrides the `data-testid`. The default `play-track-${kind}` is a class-level
     * id shared by every button of that kind (fine for table columns, where tests use
     * `.first()` or count rows). Surfaces that render a single, specifically-targeted
     * button alongside others of the same kind — e.g. the Audio File Issues inspector
     * cover, which coexists with per-card covers in split view — pass a unique id so a
     * test can address exactly one element regardless of layout.
     */
    testid?: string;
    title?: string | null;
    /**
     * Artist + album shown next to the cover in the global AudioPlayer. Both are
     * optional because not every row source carries them on the same field
     * shape — the page-level row helpers (TrackMatchingPage / EntityListPage)
     * normalize whatever they have. Pass null (or omit) and the player falls
     * back to "Unknown artist" / "Unknown album".
     */
    artist?: string | null;
    album?: string | null;
    coverUrl?: string | null;
    /**
     * Record ids behind the player bar's three identity lines. The bar links
     * each line it is given an id for, so a listener can go from "what am I
     * hearing?" to the record without hunting for the row again.
     *
     * `liveTrackCode` is not one of these: it is the audio key, not the
     * LiveTrack's id, and the inspector opens records by id. Rows that have
     * only the code pass no `liveTrackId` and the title stays plain text.
     * Raw-DDEX rows need nothing extra — their title links via
     * `rawDdexTrackId`, which is already here for playback.
     */
    liveTrackId?: number | string | null;
    artistId?: number | string | null;
    albumId?: number | string | null;
    /**
     * This row's 1-based place in the playlist it is rendered from, when it has
     * one. Only playlist surfaces pass it, and only they need to: a generated
     * playlist may name the same track three times, and this is what tells the
     * player which of the three was clicked when it adopts the list as a queue.
     */
    queuePosition?: number | null;
    /**
     * This button belongs to a row of the PLAY QUEUE, and identifies which entry.
     *
     * Changes what a plain click means: seat the player on this entry rather
     * than start a list around this track. The queue is not a list to be
     * adopted — it already IS the queue — so the play-in-context branch would
     * refetch and rebuild the very thing the user is looking at.
     *
     * It also changes how "is this the playing track" is answered. Everywhere
     * else that is a code + position comparison, because a row is identified by
     * what it names; here the entry id is exact, which matters because a queue
     * can hold the same track more than once and the rows can be reordered
     * after the positions were stamped.
     */
    queueEntryId?: string | null;
    /**
     * The full track row this button sits on, when its surface has one.
     *
     * Rides along on the entry so a queue seated on this row renders the same
     * track card the row itself does. It matters most for a ONE-track list,
     * which skips the list request entirely — without it that queue showed the
     * bare fallback row with the density buttons inert, the single case where
     * the queue was still the second-class view.
     */
    card?: PlaylistTrackRow | null;
  }>(),
  { hasAudio: true, variant: 'icon', chipSize: 'default' },
);

const playTestid = computed(() => props.testid ?? `play-track-${props.kind}`);

const chipIconClass = computed(() => (props.chipSize === 'sm' ? 'h-3 w-3' : 'h-4 w-4'));

const playback = useAudioPlaybackStore();
const { trackCodeUpper, activeDescriptorSource, isPaused, mode, currentEntry } = storeToRefs(playback);

const altHeld = useAltKeyHeld();

/**
 * Every live-track Play button offers an Option-modifier: hold Option to turn the
 * play target into "play HLS" and stream the HLS version instead of the MP3. The
 * table Play column, the cover-art overlay, and the player bar all share this, so
 * there is no separate HLS control.
 */
const optionHlsEnabled = computed(() => props.kind === 'live_track');

/** Show the circled-play (HLS) icon while Option is held over a live-track Play button. */
const showHlsIcon = computed(() => optionHlsEnabled.value && altHeld.value);

// Async work in flight for this specific button (descriptor fetch + download kickoff).
const isStarting = ref(false);

/** Player-store source slug this row maps to (Spotify never enters the in-app player). */
const playerSource = computed(() => (props.kind === 'live_track' ? 'live_mp3' : 'ddex_mp3'));

/**
 * Stable identity string handed to the player store as `code`. For live tracks it is the
 * real LiveTrack code; for raw DDEX it is a synthetic `DDEX-<id>` so the playing-row check
 * can distinguish rows. Compared upper-cased, matching how the store stores it.
 */
const playbackCode = computed(() => {
  if (props.kind === 'live_track') {
    return (props.liveTrackCode ?? '').toUpperCase();
  }
  if (props.kind === 'raw_ddex') {
    return `DDEX-${props.rawDdexTrackId ?? ''}`.toUpperCase();
  }
  return '';
});

/**
 * The record the player bar's title line opens: this row's own entity. Spotify
 * rows never reach the in-app player, so they contribute nothing.
 */
const playbackIdentityRecord = computed<{
  trackEntityId: string | null;
  trackRecordId: number | string | null;
}>(() => {
  if (props.kind === 'live_track') {
    return { trackEntityId: 'LiveTrack', trackRecordId: props.liveTrackId ?? null };
  }
  if (props.kind === 'raw_ddex') {
    return { trackEntityId: 'RawDdexTrack', trackRecordId: props.rawDdexTrackId ?? null };
  }
  return { trackEntityId: null, trackRecordId: null };
});

/**
 * Which player-store source(s) count as "this button's track". A live-track button
 * can drive either live source (MP3 by default, HLS with Option), so it matches
 * both — otherwise the pause state would drop after an Option-play.
 */
const matchesActiveSource = computed(() => {
  if (optionHlsEnabled.value) {
    return activeDescriptorSource.value === 'live_mp3' || activeDescriptorSource.value === 'live_hls';
  }
  return activeDescriptorSource.value === playerSource.value;
});

/** True when the player is currently loaded with THIS row's track. */
const isActiveTrack = computed(() => {
  if (props.kind === 'spotify' || playbackCode.value === '' || !matchesActiveSource.value) {
    return false;
  }

  // A queue row knows exactly which entry it is, so it does not have to infer
  // identity from code + position. That inference is wrong here twice over: the
  // same track can sit in a queue several times, and reordering moves rows
  // without changing the positions they were stamped with.
  if (props.queueEntryId != null) {
    return currentEntry.value?.entryId === props.queueEntryId;
  }

  if (trackCodeUpper.value !== playbackCode.value) {
    return false;
  }

  // Which OCCURRENCE. A generated playlist can name the same track three times,
  // and comparing codes alone made every one of those rows claim to be the one
  // playing: the later rows rendered Pause and paused instead of jumping.
  // `currentEntry` is null for diagnostics playback staged straight onto the bar,
  // and the predicate treats a missing position on either side as "no numbering
  // to disagree about" — which is also what keeps a restored queue (positions are
  // not persisted) showing Pause on the row that really is playing.
  return isSameQueueOccurrence(
    { code: playbackCode.value, position: props.queuePosition ?? null },
    { code: trackCodeUpper.value ?? '', position: currentEntry.value?.position ?? null },
  );
});

const isPlaying = computed(() => isActiveTrack.value && !isPaused.value && mode.value !== 'idle');
const isLoading = computed(
  () => isStarting.value || (isActiveTrack.value && mode.value === 'mp3_loading'),
);

const playable = computed(() => {
  if (props.kind === 'spotify') {
    return Boolean(props.spotifyTrackId);
  }
  if (props.hasAudio === false) {
    return false;
  }
  return props.kind === 'live_track'
    ? Boolean(props.liveTrackCode)
    : Boolean(props.rawDdexTrackId);
});

const tooltip = computed(() => {
  if (!playable.value) {
    return 'No audio available';
  }
  if (props.kind === 'spotify') {
    return 'Open in Spotify';
  }
  if (isPlaying.value) {
    return 'Pause';
  }
  if (isLoading.value) {
    return 'Loading…';
  }
  return showHlsIcon.value ? 'Play HLS' : 'Play';
});

/** Open the Spotify desktop/mobile app without navigating the SPA. */
function openInSpotify() {
  const id = props.spotifyTrackId;
  if (!id) {
    return;
  }
  const link = document.createElement('a');
  link.href = `spotify://track/${id}`;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

const getQueueContext = useTrackQueueContext();

/** This row as a queue entry — the same shape whichever gesture is used. */
function buildEntryForThisRow(source: 'live_mp3' | 'live_hls' | 'ddex_mp3'): QueueEntry {
  return buildQueueEntry({
    identity:
      props.kind === 'live_track'
        ? { kind: 'live_track', code: props.liveTrackCode as string }
        : { kind: 'raw_ddex', rawDdexTrackId: Number(props.rawDdexTrackId) },
    source,
    code: playbackCode.value,
    title: props.title ?? null,
    artist: props.artist ?? null,
    album: props.album ?? null,
    coverUrl: props.coverUrl ?? null,
    position: props.queuePosition ?? null,
    // Every path that plays or queues this row goes through here, so the bar's
    // links come along whether the row was played, shift-enqueued, or seated as
    // the head of a play-in-context queue.
    ...playbackIdentityRecord.value,
    artistId: props.artistId ?? null,
    albumId: props.albumId ?? null,
  });
}

/**
 * Shift-click appends this one track to the queue.
 *
 * A per-track gesture, so it works from ANY surface — including Track Matching,
 * which is not a listening context and therefore has no play-in-context.
 */
async function enqueueThisRow(): Promise<void> {
  const entry = buildEntryForThisRow(props.kind === 'live_track' ? 'live_mp3' : 'ddex_mp3');

  isStarting.value = true;
  try {
    const appended = await playback.enqueue(entry);
    if (!appended) {
      toast.error('The queue is full.');
      return;
    }
    toast.success('Added to queue.', { description: props.title ?? playbackCode.value });
  } finally {
    isStarting.value = false;
  }
}

async function onClick(event?: MouseEvent) {
  if (!playable.value || isLoading.value) {
    return;
  }

  if (props.kind === 'spotify') {
    // Checked before the Shift branch: Spotify audio is not ours to serve, so
    // it can never enter the queue by any gesture.
    openInSpotify();
    return;
  }

  if (event?.shiftKey) {
    await enqueueThisRow();
    return;
  }

  // A queue row: seat the player on THIS entry, or pause it if it is already
  // the one playing. Checked before every branch below because none of them
  // applies to a queue — there is no list to adopt around a track that is
  // already queued, and jumping is not a re-load.
  if (props.queueEntryId != null) {
    if (isActiveTrack.value) {
      await playback.togglePlay();

      return;
    }

    isStarting.value = true;
    try {
      await playback.jumpToEntry(props.queueEntryId);
    } finally {
      isStarting.value = false;
    }

    return;
  }

  // Checked BEFORE play-in-context: this row is the loaded track, so the button
  // is rendering a Pause icon and saying "Pause". Falling through to the queue
  // branch would refetch the whole list and restart the track from 0:00 — a
  // control that says Pause and does Restart. The one exception is the
  // Option-request to switch to HLS, which is a real re-load.
  if (props.kind === 'live_track' && isActiveTrack.value) {
    const wantsHlsSwitch =
      optionHlsEnabled.value &&
      (event?.altKey ?? altHeld.value) &&
      activeDescriptorSource.value !== 'live_hls';

    if (!wantsHlsSwitch) {
      await playback.togglePlay();
      return;
    }
  }

  // Plain click on a listenable list: the whole list becomes the queue.
  const queueContext = getQueueContext();
  if (queueContext !== null && props.kind === 'live_track' && !(event?.altKey ?? false)) {
    isStarting.value = true;
    try {
      const loadedList = await playTrackListInContext(
        playback,
        buildEntryForThisRow('live_mp3'),
        queueContext,
      );

      if (!loadedList) {
        toast.error('Could not load the rest of the list.');
      }
    } finally {
      isStarting.value = false;
    }
    return;
  }

  if (props.kind === 'live_track') {
    // Hold Option to stream HLS instead of the MP3. The pause/resume branch for
    // the active track has already run above, so anything reaching here either
    // is not the active track or is an explicit Option-switch to HLS.
    const wantHls = optionHlsEnabled.value && (event?.altKey ?? altHeld.value);
    const source = wantHls ? 'live_hls' : 'live_mp3';
    isStarting.value = true;
    try {
      await playback.loadAndPlay({
        code: props.liveTrackCode as string,
        title: props.title ?? null,
        artist: props.artist ?? null,
        album: props.album ?? null,
        coverUrl: props.coverUrl ?? null,
        ...playbackIdentityRecord.value,
        artistId: props.artistId ?? null,
        albumId: props.albumId ?? null,
        source,
        position: props.queuePosition ?? null,
      });
    } catch {
      toast.error('Could not play this track.');
    } finally {
      isStarting.value = false;
    }
    return;
  }

  // raw_ddex — single MP3 source.
  if (isActiveTrack.value) {
    await playback.togglePlay();
    return;
  }
  isStarting.value = true;
  try {
    // Hand the store the IDENTITY, not a descriptor fetched here: it resolves one
    // itself (exactly once), and the entry stays re-resolvable — so an expired
    // presigned URL self-heals on retry and the entry survives a reload.
    await playback.loadAndPlayEntry(buildEntryForThisRow('ddex_mp3'));
  } catch {
    // The player store records errorMessage; surface a toast for the table context.
    toast.error('Could not play this track.');
  } finally {
    isStarting.value = false;
  }
}
</script>

<template>
  <Tooltip v-if="variant === 'cover-overlay'">
    <TooltipTrigger as-child>
      <button
        type="button"
        class="group/playoverlay absolute inset-0 flex cursor-pointer items-center justify-center focus-visible:outline-none disabled:cursor-default"
        :disabled="!playable"
        :aria-label="tooltip"
        :data-testid="playTestid"
        @click.stop="onClick"
      >
        <!-- Reveal on hover or KEYBOARD focus only (:focus-visible): plain :focus would
             pin the chip after a mouse click until something else takes focus. -->
        <span
          class="bg-background/85 border-border flex items-center justify-center rounded-full border shadow-sm transition-opacity"
          :class="[
            chipSize === 'sm' ? 'h-5 w-5' : 'h-7 w-7',
            isPlaying || isLoading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible/playoverlay:opacity-100',
          ]"
        >
          <ArrowPathIcon v-if="isLoading" class="animate-spin" :class="chipIconClass" />
          <PauseIcon v-else-if="isPlaying" :class="chipIconClass" />
          <PlayCircleIcon v-else-if="showHlsIcon" :class="chipIconClass" />
          <PlayIcon v-else :class="chipIconClass" />
        </span>
      </button>
    </TooltipTrigger>
    <TooltipContent>{{ tooltip }}</TooltipContent>
  </Tooltip>
  <Tooltip v-else>
    <TooltipTrigger as-child>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        class="h-7 w-7 shrink-0"
        :disabled="!playable"
        :aria-label="tooltip"
        :data-testid="playTestid"
        @click.stop="onClick"
      >
        <ArrowPathIcon v-if="isLoading" class="h-4 w-4 animate-spin" />
        <PauseIcon v-else-if="isPlaying" class="h-4 w-4" />
        <!-- Option-HLS uses a circled play to read as a stream, not the bare MP3 triangle. -->
        <PlayCircleIcon v-else-if="showHlsIcon" class="h-4 w-4" />
        <PlayIcon v-else class="h-4 w-4" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{{ tooltip }}</TooltipContent>
  </Tooltip>
</template>
